
interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  address?: string;
}

// Function to get current geolocation
export const getCurrentLocation = (): Promise<LocationData> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("La géolocalisation n'est pas supportée par votre navigateur"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        resolve(locationData);
      },
      (error) => {
        let errorMessage = "Erreur de géolocalisation inconnue";
        
        switch (error.code) {
          case 1:
            errorMessage = "Permission de géolocalisation refusée";
            break;
          case 2:
            errorMessage = "Position indisponible";
            break;
          case 3:
            errorMessage = "Délai d'attente dépassé";
            break;
        }
        
        reject(new Error(errorMessage));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

// Function to convert coordinates to address using Nominatim API (OpenStreetMap)
export const getAddressFromCoordinates = async (lat: number, lng: number): Promise<string> => {
  try {
    // Utilisation de l'API Nominatim d'OpenStreetMap (gratuite et plus précise)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=fr`,
      {
        headers: {
          'User-Agent': 'AVEM-App/1.0 (Contact: admin@avem.fr)'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de l'adresse");
    }
    
    const data = await response.json();
    
    if (data && data.display_name) {
      // Formatage de l'adresse pour plus de lisibilité
      const address = data.address;
      let formattedAddress = "";
      
      // Construction d'une adresse structurée
      const parts = [];
      
      if (address.house_number && address.road) {
        parts.push(`${address.house_number} ${address.road}`);
      } else if (address.road) {
        parts.push(address.road);
      }
      
      if (address.suburb || address.neighbourhood) {
        parts.push(address.suburb || address.neighbourhood);
      }
      
      if (address.postcode && address.city) {
        parts.push(`${address.postcode} ${address.city}`);
      } else if (address.city || address.town || address.village) {
        parts.push(address.city || address.town || address.village);
      }
      
      if (address.country) {
        parts.push(address.country);
      }
      
      formattedAddress = parts.filter(part => part).join(", ");
      
      // Si on n'a pas réussi à construire une adresse structurée, utiliser display_name
      if (!formattedAddress) {
        formattedAddress = data.display_name;
      }
      
      return formattedAddress;
    } else {
      // Fallback vers l'ancienne méthode si Nominatim ne fonctionne pas
      return getEstimatedAddress(lat, lng);
    }
    
  } catch (error) {
    console.error("Erreur de géocodage Nominatim:", error);
    
    // Fallback vers l'estimation approximative
    try {
      return getEstimatedAddress(lat, lng);
    } catch (fallbackError) {
      console.error("Erreur de géocodage fallback:", fallbackError);
      return `Position: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  }
};

// Fonction de fallback pour l'estimation d'adresse (méthode existante améliorée)
const getEstimatedAddress = (lat: number, lng: number): string => {
  // Coordonnées plus précises de villes françaises avec plus de détails
  const cities = [
    { name: "Paris", lat: 48.8566, lng: 2.3522, region: "Île-de-France" },
    { name: "Marseille", lat: 43.2965, lng: 5.3698, region: "Provence-Alpes-Côte d'Azur" },
    { name: "Lyon", lat: 45.7640, lng: 4.8357, region: "Auvergne-Rhône-Alpes" },
    { name: "Toulouse", lat: 43.6045, lng: 1.4440, region: "Occitanie" },
    { name: "Nice", lat: 43.7101, lng: 7.2620, region: "Provence-Alpes-Côte d'Azur" },
    { name: "Nantes", lat: 47.2184, lng: -1.5536, region: "Pays de la Loire" },
    { name: "Strasbourg", lat: 48.5734, lng: 7.7521, region: "Grand Est" },
    { name: "Montpellier", lat: 43.6112, lng: 3.8767, region: "Occitanie" },
    { name: "Bordeaux", lat: 44.8378, lng: -0.5792, region: "Nouvelle-Aquitaine" },
    { name: "Lille", lat: 50.6292, lng: 3.0573, region: "Hauts-de-France" },
    { name: "Grenoble", lat: 45.1885, lng: 5.7245, region: "Auvergne-Rhône-Alpes" },
    { name: "Rennes", lat: 48.1173, lng: -1.6778, region: "Bretagne" },
    { name: "Reims", lat: 49.2583, lng: 4.0317, region: "Grand Est" },
    { name: "Le Havre", lat: 49.4944, lng: 0.1079, region: "Normandie" },
    { name: "Saint-Étienne", lat: 45.4397, lng: 4.3872, region: "Auvergne-Rhône-Alpes" },
    { name: "Toulon", lat: 43.1242, lng: 5.9280, region: "Provence-Alpes-Côte d'Azur" },
    { name: "Angers", lat: 47.4784, lng: -0.5632, region: "Pays de la Loire" },
    { name: "Dijon", lat: 47.3220, lng: 5.0415, region: "Bourgogne-Franche-Comté" },
    { name: "Brest", lat: 48.3904, lng: -4.4861, region: "Bretagne" },
    { name: "Le Mans", lat: 48.0060, lng: 0.1996, region: "Pays de la Loire" }
  ];
  
  // Calcul de la distance entre deux points en km (formule de Haversine)
  const getDistanceFromLatLonInKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const d = R * c;
    return d;
  };
  
  // Trouver la ville la plus proche
  let closestCity = cities[0];
  let minDistance = getDistanceFromLatLonInKm(lat, lng, closestCity.lat, closestCity.lng);
  
  for (let i = 1; i < cities.length; i++) {
    const distance = getDistanceFromLatLonInKm(lat, lng, cities[i].lat, cities[i].lng);
    if (distance < minDistance) {
      minDistance = distance;
      closestCity = cities[i];
    }
  }
  
  // Déterminer la direction relative plus précise
  const getDirection = (lat1: number, lon1: number, lat2: number, lon2: number): string => {
    const directions = ["Nord", "Nord-Est", "Est", "Sud-Est", "Sud", "Sud-Ouest", "Ouest", "Nord-Ouest"];
    const angle = Math.atan2(lat2 - lat1, lon2 - lon1) * 180 / Math.PI;
    const index = Math.round(((angle + 180) % 360) / 45) % 8;
    return directions[index];
  };
  
  const direction = getDirection(closestCity.lat, closestCity.lng, lat, lng);
  
  // Coordonnées formatées en degrés, minutes, secondes
  const formatCoordinate = (coordinate: number, isLatitude: boolean): string => {
    const absolute = Math.abs(coordinate);
    const degrees = Math.floor(absolute);
    const minutesNotTruncated = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesNotTruncated);
    const seconds = Math.floor((minutesNotTruncated - minutes) * 60);
    
    const directionSuffix = isLatitude 
      ? (coordinate >= 0 ? 'N' : 'S') 
      : (coordinate >= 0 ? 'E' : 'O');
    
    return `${degrees}°${minutes}'${seconds}"${directionSuffix}`;
  };
  
  const latitude = formatCoordinate(lat, true);
  const longitude = formatCoordinate(lng, false);
  
  if (minDistance < 2) {
    return `Centre-ville de ${closestCity.name}, ${closestCity.region} (${latitude}, ${longitude})`;
  } else if (minDistance < 10) {
    return `${Math.round(minDistance)} km ${direction} de ${closestCity.name}, ${closestCity.region} (${latitude}, ${longitude})`;
  } else if (minDistance < 50) {
    return `${Math.round(minDistance)} km ${direction} de ${closestCity.name}, région ${closestCity.region} (${latitude}, ${longitude})`;
  } else {
    return `Région à ${Math.round(minDistance)} km ${direction} de ${closestCity.name}, ${closestCity.region} (${latitude}, ${longitude})`;
  }
};
